/* eslint-disable i18next/no-literal-string */
import {useState} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {usePageTitle} from '@/hooks/use-page-title'
import {useTypedEntityAttributes} from '@/hooks/use-typed-entity-attributes'
import {pickTypeValueLabel} from '@/lib/promoted-types-label'
import {routes} from '@/lib/routes'
import {Button} from '@/components/ui/button'
import {Card, CardHeader, CardTitle, CardContent} from '@/components/ui/card'
import {InlineRow} from '@/components/shared/InlineRow'
import {FormGrid} from '@/components/shared/FormGrid'
import {PageBackButton} from '@/components/shared/PageBackButton'
import {SectionContainer} from '@/components/shared/SectionContainer'
import {AttributeFields} from '@/components/attributes/AttributeFields'
import {AttributeEntityType} from '@/types/attribute-entity-type'
import {AttributeValueType} from '@/types/attribute'
import type {AttributeDefinition, SetAttributeRequest} from '@/types/attribute-definition'
import type {TargetSystemCreateRequest} from '@/types/system'
import {TargetSystemResponseFields} from '@/types/target-system'
import {useFormSubmit} from '@/hooks/use-form-submit'

function buildSetAttributeRequest(def: AttributeDefinition, raw: string): SetAttributeRequest {
    const value = raw.trim()
    switch (def.valueType) {
        case AttributeValueType.NUMBER:
            return {attributeName: def.name, valueType: def.valueType, valueNumber: Number(value)}
        case AttributeValueType.BOOLEAN:
            // eslint-disable-next-line i18next/no-literal-string -- boolean string-form sentinel from <input>
            return {attributeName: def.name, valueType: def.valueType, valueBoolean: value === 'true'}
        case AttributeValueType.DATE:
            return {attributeName: def.name, valueType: def.valueType, valueDate: value}
        case AttributeValueType.TIMESTAMP:
            return {attributeName: def.name, valueType: def.valueType, valueTimestamp: value}
        case AttributeValueType.STRING:
        case AttributeValueType.JSON:
        case AttributeValueType.REFERENCE:
            return {attributeName: def.name, valueType: def.valueType, valueString: value}
    }
}

/**
 * Create Target System page invoked from /systems/list/by-type/{TYPE}/new.
 * The systemType is locked from the URL; the form collects environment +
 * name + optional externalId + the type-scoped dynamic attributes.
 */
export function CreateTargetSystem() {
    const {typeCode} = useParams<{typeCode: string}>()
    const services = useServices()
    const i18n = useI18n()
    const navigate = useNavigate()
    const {systemTypes} = useAppConfig()
    const {i18n: i18nInstance} = useTranslation()
    const lang = i18nInstance.language

    const systemTypeCode = (typeCode ?? '').toUpperCase()
    const singularLabel = pickTypeValueLabel(systemTypes, systemTypeCode, lang) ?? labelOf(systemTypes, systemTypeCode)
    const pageTitle = `${i18n.common.create} ${singularLabel}`

    usePageTitle(pageTitle)

    const [attributeValues, setAttributeValues] = useState<Record<string, string>>({})

    const {formDefinitions, templateAttributeNames, isLoading} = useTypedEntityAttributes({
        entityType: AttributeEntityType.TARGET_SYSTEM,
        typeCode: systemTypeCode,
    })

    const renderDefinitions = formDefinitions.filter(d => d.name !== TargetSystemResponseFields.systemType)

    const createMutation = useEntityMutation({
        mutationFn: (request: TargetSystemCreateRequest) => services.systems.create(request),
        successMessage: i18n.systems.createSuccess,
        onSuccess: (system) => {
            navigate(routes.systems.detail(system.id))
        },
    })

    const {submit, fieldErrors} = useFormSubmit({
        fields: renderDefinitions.map((def) => {
            const label = def.displayName || def.name
            const value = attributeValues[def.id] ?? ''
            const focusId = def.name === TargetSystemResponseFields.name
                ? 'input-system-name'
                : def.name === TargetSystemResponseFields.environment
                ? 'select-environment'
                : `attr-${def.name}`
            const isStaticRequired =
                def.name === TargetSystemResponseFields.name ||
                def.name === TargetSystemResponseFields.environment
            return {
                id: def.id,
                label,
                value,
                required: isStaticRequired || def.required || templateAttributeNames.includes(def.name),
                focusId,
            }
        }),
        onSubmit: () => {
            const getVal = (fieldName: string) => {
                const def = formDefinitions.find(d => d.name === fieldName)
                return def ? (attributeValues[def.id] ?? '').trim() : ''
            }
            const nameVal = getVal(TargetSystemResponseFields.name)
            const environmentVal = getVal(TargetSystemResponseFields.environment)

            const staticNames: string[] = [TargetSystemResponseFields.name, TargetSystemResponseFields.systemType, TargetSystemResponseFields.environment]
            const dynamicDefs = formDefinitions.filter(def => !staticNames.includes(def.name))

            const attributes: SetAttributeRequest[] = dynamicDefs
                .map((def) => {
                    const raw = attributeValues[def.id] ?? ''
                    if (!raw.trim()) return null
                    return buildSetAttributeRequest(def, raw)
                })
                .filter((r): r is SetAttributeRequest => r != null)

            createMutation.mutate({
                systemType: systemTypeCode,
                environment: environmentVal,
                name: nameVal,
                externalId: null,
                attributes,
            })
        },
    })

    return (
        <SectionContainer>
            <PageBackButton to={routes.systems.byType(systemTypeCode)}/>

            <Card className="max-w-2xl">
                <CardHeader>
                    <CardTitle data-testid="card-title-create-system">{pageTitle}</CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={submit}>
                        <FormGrid>
                            <AttributeFields
                                definitions={renderDefinitions}
                                values={attributeValues}
                                onChange={(defId, v) =>
                                    setAttributeValues((s) => ({...s, [defId]: v}))}
                                templateAttributeNames={templateAttributeNames}
                                errors={fieldErrors}
                                testIdMap={{
                                    name: 'input-system-name',
                                    environment: 'select-environment',
                                }}
                            />
                        </FormGrid>

                        <InlineRow className="justify-end pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                data-testid="btn-cancel-create-system"
                                onClick={() => navigate(routes.systems.byType(systemTypeCode))}
                            >
                                {i18n.common.cancel}
                            </Button>
                            <Button
                                type="submit"
                                data-testid="btn-submit-create-system"
                                disabled={isLoading || createMutation.isPending}
                            >
                                {pageTitle}
                            </Button>
                        </InlineRow>
                    </form>
                </CardContent>
            </Card>
        </SectionContainer>
    )
}

// WHY: testid generator can't follow `focusId = ... ? 'kebab-string' : ...`
// variables — the data-testid is rendered by AttributeFields/AttributeValueInput
// from htmlFor strings assembled here. Register explicitly via the
// `scan-testid:` annotation pattern (issue 1023).
// scan-testid: input-system-name
// scan-testid: select-environment
// scan-testid-template: env-${env_code}
